import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Group,
  Button,
  Paper,
  Text,
  SimpleGrid,
  Badge,
  LoadingOverlay,
  Alert,
} from "@mantine/core";
import {
  IconExternalLink,
  IconCalendar,
  IconLogin,
  IconLogout,
  IconCoffee,
  IconAlertCircle,
} from "@tabler/icons-react";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import moment from "moment-timezone";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { usePayrollPeriod } from "../../hooks/usePayrollPeriod";
import { openPayrollDashboard } from "../../utils/payrollUtils";
import { Employee } from "../../types/salon";
import { BusinessInfo } from "../../types/context";
import {
  TimeTrackingState,
  TimeTrackingStatusResponse,
} from "../../types/timeTracking";

interface SalonContextValue {
  currentUser: Employee | null;
  businessInfo: BusinessInfo | null;
  employees: Employee[];
  isLoading: boolean;
}

const DEFAULT_STATUS: TimeTrackingState = {
  isClockedIn: false,
  isOnBreak: false,
};

export default function EmployeeOverviewView() {
  const { isMobile } = useResponsive();
  const salonContext = useSalon() as unknown as SalonContextValue;
  const { currentUser, businessInfo, isLoading: salonLoading } = salonContext;

  const typedBusinessInfo = businessInfo as BusinessInfo | null;
  const payrollPeriod = usePayrollPeriod(typedBusinessInfo);

  const { getTimeTrackingStatus, performTimeTrackingAction } = useApp();

  const companyId = typedBusinessInfo?.rollfi__company_id || "";
  const timezone = typedBusinessInfo?.timezone || "America/New_York";

  const [trackingState, setTrackingState] =
    useState<TimeTrackingState>(DEFAULT_STATUS);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    if (!currentUser?.rollfi__user_id) {
      setError("Your account is not set up for time tracking");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const status: TimeTrackingStatusResponse = await getTimeTrackingStatus(
        currentUser.rollfi__user_id,
        companyId,
      );

      setTrackingState({
        isClockedIn: status.isClockedIn,
        isOnBreak: status.isOnBreak,
        clockInTime: status.clockInTime,
        breakStartTime: status.breakStartTime,
      });
    } catch (err) {
      console.error("Error fetching time tracking status:", err);
      const message =
        err instanceof Error
          ? err.message
          : "Failed to fetch time tracking status";
      setError(
        message.includes("Validation error")
          ? "Time tracking is not available"
          : message,
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.rollfi__user_id, getTimeTrackingStatus]);

  useEffect(() => {
    if (!salonLoading && currentUser?.rollfi__user_id) {
      fetchStatus();
    } else if (!salonLoading) {
      setIsLoading(false);
    }
  }, [salonLoading, currentUser?.rollfi__user_id, fetchStatus]);

  const handleClockIn = async () => {
    if (!currentUser?.rollfi__user_id) return;

    try {
      setIsActionLoading(true);
      const response = await performTimeTrackingAction({
        method: "clockIn",
        companyId,
        userId: currentUser.rollfi__user_id,
      });

      const clockInResp =
        response as import("../../types/timeTracking").ClockInResponse;
      setTrackingState({
        isClockedIn: true,
        isOnBreak: false,
        clockInTime: new Date().toISOString(),
      });
      notifySuccess(clockInResp.timeEntry.message, { title: "Clocked In" });
    } catch (err) {
      console.error("Error clocking in:", err);
      notifyError(err instanceof Error ? err.message : "Failed to clock in", {
        title: "Error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!currentUser?.rollfi__user_id || !trackingState.isClockedIn) return;

    try {
      setIsActionLoading(true);
      const response = await performTimeTrackingAction({
        method: "clockOut",
        companyId,
        userId: currentUser.rollfi__user_id,
      });

      const clockOutResp =
        response as import("../../types/timeTracking").ClockOutResponse;
      setTrackingState({
        isClockedIn: false,
        isOnBreak: false,
      });
      notifySuccess(clockOutResp.timeEntry.message, { title: "Clocked Out" });
    } catch (err) {
      console.error("Error clocking out:", err);
      notifyError(err instanceof Error ? err.message : "Failed to clock out", {
        title: "Error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStartBreak = async () => {
    if (!currentUser?.rollfi__user_id || !trackingState.isClockedIn) return;

    try {
      setIsActionLoading(true);
      const response = await performTimeTrackingAction({
        method: "startBreak",
        userId: currentUser.rollfi__user_id,
        companyId,
      });

      const breakResp =
        response as import("../../types/timeTracking").StartBreakResponse;
      setTrackingState({
        ...trackingState,
        isOnBreak: true,
        breakStartTime: new Date().toISOString(),
      });
      notifyWarning(breakResp.timeEntryBreak.message, {
        title: "Break Started",
      });
    } catch (err) {
      console.error("Error starting break:", err);
      notifyError(
        err instanceof Error ? err.message : "Failed to start break",
        { title: "Error" },
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleEndBreak = async () => {
    if (!currentUser?.rollfi__user_id || !trackingState.isOnBreak) return;

    try {
      setIsActionLoading(true);
      const response = await performTimeTrackingAction({
        method: "endBreak",
        userId: currentUser.rollfi__user_id,
        companyId,
      });

      const breakResp =
        response as import("../../types/timeTracking").EndBreakResponse;
      setTrackingState({
        ...trackingState,
        isOnBreak: false,
        breakStartTime: undefined,
      });
      notifySuccess(breakResp.timeEntryBreak.message, { title: "Break Ended" });
    } catch (err) {
      console.error("Error ending break:", err);
      notifyError(err instanceof Error ? err.message : "Failed to end break", {
        title: "Error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Get status badge properties
  const getStatusBadgeProps = () => {
    if (trackingState.isOnBreak) {
      return { color: "orange", label: "On Break" };
    }
    if (trackingState.isClockedIn) {
      return { color: "teal", label: "Clocked In" };
    }
    return { color: "gray", label: "Clocked Out" };
  };

  const statusBadge = getStatusBadgeProps();

  const formatTime = (timeString: string | undefined) => {
    if (!timeString) return "";
    return moment(timeString).tz(timezone).format("h:mm A");
  };

  if (error) {
    return (
      <Stack gap="lg">
        <Paper p="md" withBorder>
          <Group justify="space-between" align="center" wrap="wrap">
            <div>
              <Text size="lg" fw={600}>
                Payroll Portal
              </Text>
              <Text size="sm" c="dimmed">
                Access your RollFi payroll dashboard
              </Text>
            </div>
            <Button
              leftSection={<IconExternalLink size={18} />}
              onClick={openPayrollDashboard}
              color="teal"
              size={isMobile ? "sm" : "md"}
            >
              Open Payroll Portal
            </Button>
          </Group>
        </Paper>

        <Alert color="red" title="Error" icon={<IconAlertCircle />}>
          {error}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" style={{ position: "relative" }}>
      <LoadingOverlay visible={isLoading || salonLoading} />

      {/* Payroll Portal Button - Prominent at top */}
      <Paper p="md" withBorder>
        <Group justify="space-between" align="center" wrap="wrap">
          <div>
            <Text size="lg" fw={600}>
              Payroll Portal
            </Text>
            <Text size="sm" c="dimmed">
              Access your RollFi payroll dashboard
            </Text>
          </div>
          <Button
            leftSection={<IconExternalLink size={18} />}
            onClick={openPayrollDashboard}
            color="teal"
            size={isMobile ? "sm" : "md"}
          >
            Open Payroll Portal
          </Button>
        </Group>
      </Paper>

      {/* Dashboard Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {/* Pay Period Card */}
        <Paper p="md" withBorder>
          <Group gap="sm" mb="sm">
            <IconCalendar size={20} color="var(--mantine-color-teal-6)" />
            <Text fw={600}>Current Pay Period</Text>
          </Group>
          {payrollPeriod ? (
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                {moment(payrollPeriod.payBeginDate).format("MMM D, YYYY")} -{" "}
                {moment(payrollPeriod.payEndDate).format("MMM D, YYYY")}
              </Text>
              <Group gap="xs">
                <Badge color="teal" variant="light" size="sm">
                  Pay Date: {moment(payrollPeriod.payDate).format("MMM D")}
                </Badge>
              </Group>
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              Loading pay period...
            </Text>
          )}
        </Paper>

        {/* Status Card */}
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600}>Your Status</Text>
            <Badge color={statusBadge.color} variant="light" size="lg">
              {statusBadge.label}
            </Badge>
          </Group>
          {trackingState.isClockedIn && trackingState.clockInTime && (
            <Text size="sm" c="dimmed">
              Clocked in at {formatTime(trackingState.clockInTime)}
            </Text>
          )}
          {trackingState.isOnBreak && trackingState.breakStartTime && (
            <Text size="sm" c="dimmed">
              Break started at {formatTime(trackingState.breakStartTime)}
            </Text>
          )}
        </Paper>
      </SimpleGrid>

      {/* Clock Action Buttons */}
      <Paper p="md" withBorder>
        <Text fw={600} mb="md">
          Time Clock
        </Text>
        <Group gap="sm" wrap="wrap">
          {!trackingState.isClockedIn && (
            <Button
              color="teal"
              leftSection={<IconLogin size={18} />}
              onClick={handleClockIn}
              loading={isActionLoading}
              size={isMobile ? "sm" : "md"}
            >
              Clock In
            </Button>
          )}

          {trackingState.isClockedIn && !trackingState.isOnBreak && (
            <>
              <Button
                color="orange"
                leftSection={<IconCoffee size={18} />}
                onClick={handleStartBreak}
                loading={isActionLoading}
                size={isMobile ? "sm" : "md"}
              >
                Start Break
              </Button>
              <Button
                color="red"
                variant="light"
                leftSection={<IconLogout size={18} />}
                onClick={handleClockOut}
                loading={isActionLoading}
                size={isMobile ? "sm" : "md"}
              >
                Clock Out
              </Button>
            </>
          )}

          {trackingState.isOnBreak && (
            <Button
              color="teal"
              leftSection={<IconCoffee size={18} />}
              onClick={handleEndBreak}
              loading={isActionLoading}
              size={isMobile ? "sm" : "md"}
            >
              End Break
            </Button>
          )}
        </Group>
      </Paper>
    </Stack>
  );
}
